import { memo, useMemo } from 'react'
import { MlImages } from '../ml-images'
import { MlGeneralWorkspaceCardProps } from '../ml-general-workspace-card'
import { AtIcon, AtTag, AtTagProps } from '../../atoms'

export * from './skeleton'

export type SpaceCardTagProps = AtTagProps & { position: 'left' | 'right' }

export interface MlSpaceCardProps extends MlGeneralWorkspaceCardProps {
  space: string
  building: string
  details?: { icon: string; label: string; name?: string }[]
  /** @deprecated Use tags instead for multiple badges */
  tag?: AtTagProps
  /** Badges to display: FEATURED (left), ON-DEMAND (right) */
  tags?: SpaceCardTagProps[]
  rate?: string
  rateSubtitle?: string
  size?: 'small' | 'medium'
  onClick?: () => void
  /** When true, card images use lazy loading to defer off-screen images */
  lazy?: boolean
}

const tagPositionClasses = { left: 'left-6 top-6', right: 'right-6 top-6' } as const

const MlSpaceCardComponent = ({
  building,
  details,
  href,
  images,
  rate = 'Inquire for Pricing',
  rateSubtitle,
  size = 'small',
  space,
  tag,
  tags,
  title,
  onClick,
  lazy,
}: MlSpaceCardProps) => {
  const effectiveTags = useMemo<SpaceCardTagProps[]>(
    () => tags ?? (tag ? [{ ...tag, position: 'left' as const }] : []),
    [tags, tag]
  )
  return (
    <div
      onClick={onClick}
      data-product
      className={`h-fit ${
        size === 'medium' ? 'w-[21.5rem] sm:w-[22.425rem]  slg:w-[14.219rem] lg:w-[17.58rem]' : 'w-[17.88rem]'
      } relative rounded-4 border-1 border-transparent hover:border-neutrals-400 active:border-neutrals-600 [&:has(a:focus-visible)]:border-neutrals-900 [&:has(a:focus-visible)]:rounded-0`}
    >
      {effectiveTags.map((t, i) => {
        const { position, ...tagProps } = t
        return <AtTag key={i} {...tagProps} className={`absolute z-[2] ${tagPositionClasses[position]}`} />
      })}
      <a title={title} href={href} className="h-full p-2 lg:p-4 flex flex-col focus:outline-none relative z-[2]">
        {/* This div fills the space the image occupies */}
        <div className="mb-2 lg:mb-4 aspect-[4/3]" />
        <p className="font-heading font-medium text-[1.25rem] slg:text-20 leading-1.4 text-neutrals-900 line-clamp-1">
          {title}
        </p>
        <p className="font-regular text-14 leading-1.5 mt-1 text-neutrals-800 md:whitespace-nowrap md:overflow-hidden md:text-ellipsis">
          {space} at {building}
        </p>
        <div className="h-20 my-2 text-neutrals-800 text-14 leading-1.5 font-bold">
          {details?.map(({ label, icon, name }, i) => (
            <div data-detail={name} key={i} className="h-10 flex items-center">
              <AtIcon src={icon} />
              {label}
            </div>
          ))}
        </div>
        <p data-detail="rate" className="font-heading text-20 leading-1.3">
          {rate}
        </p>
        {rateSubtitle ? (
          <p data-detail="rate-subtitle" className="text-16 pt-1 text-neutrals-800">
            {rateSubtitle}
          </p>
        ) : null}
      </a>
      <div
        className={`absolute top-2 left-2 lg:top-4 lg:left-4 aspect-[4/3] ${
          size === 'small' ? 'w-[94%] lg:w-[89%]' : ' w-[95.1%] slg:w-[93%] lg:w-[89%]'
        }`}
      >
        <MlImages unsetMaxDimensions aspectRatio="4:3" images={images} href={href} lazy={lazy} />
      </div>
    </div>
  )
}

export const MlSpaceCard = memo(MlSpaceCardComponent)
